import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileCheck2, Loader2, AlertCircle } from "lucide-react";
import type { NotaFiscal } from "@/data/mock";

interface Props {
  nf: Pick<NotaFiscal, "id" | "numero">;
  open: boolean;
  onClose: () => void;
  onSuccess: (nfAtualizada: NotaFiscal) => void;
}

export const CancelarNFModal = ({ nf, open, onClose, onSuccess }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);

  const handleClose = () => {
    if (enviando) return;
    setArquivo(null);
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setArquivo(file);
    e.target.value = "";
  };

  const handleEnviar = async () => {
    if (!arquivo) {
      toast.error("Selecione o arquivo da nota cancelada");
      return;
    }
    const fd = new FormData();
    fd.append("arquivo", arquivo);
    setEnviando(true);
    try {
      const nfAtualizada = await api.postForm<NotaFiscal>(`/nfs/${nf.id}/cancelar`, fd);
      toast.success("Nota cancelada validada com sucesso!");
      onSuccess(nfAtualizada);
      handleClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao validar cancelamento");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Enviar nota cancelada — NF nº {nf.numero}</DialogTitle>
          <DialogDescription>
            Envie o arquivo da nota fiscal com a marca d'água ou carimbo de <strong>CANCELADA</strong>.
            A IA irá validar o cancelamento automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 flex gap-2 text-sm text-amber-700 dark:text-amber-400">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            O documento deve conter visualmente: <strong>CANCELADA</strong>, <strong>CANCELADO</strong>, <strong>NULA</strong> ou equivalente.
          </span>
        </div>

        <label
          className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-8 text-sm transition-colors
            ${enviando ? "cursor-not-allowed opacity-60" : "hover:border-primary/50 hover:bg-primary/5"}
            ${arquivo ? "border-primary/40 bg-primary/5" : "border-input bg-muted/30"}`}
        >
          {arquivo ? (
            <>
              <FileCheck2 className="h-7 w-7 text-primary" />
              <span className="font-medium text-primary">{arquivo.name}</span>
              <span className="text-xs text-muted-foreground">Clique para trocar</span>
            </>
          ) : (
            <>
              <Upload className="h-7 w-7 text-muted-foreground" />
              <span className="font-medium">Clique para selecionar</span>
              <span className="text-xs text-muted-foreground">PDF, PNG ou JPG — máx. 10 MB</span>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            disabled={enviando}
            onChange={handleFileChange}
          />
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={handleClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={handleEnviar} disabled={!arquivo || enviando}>
            {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {enviando ? "Validando..." : "Enviar para validação"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
